<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  groups: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: 'canned',
  },
});

const emit = defineEmits(['mentionSelect']);

const { getPlainText } = useMessageFormatter();

const mentionsListContainerRef = ref(null);
const selectedIndex = ref(0);
const activeTabIndex = ref(0);

const hasGroups = computed(() => props.groups && props.groups.length > 0);

// When groups change, reset the active tab to the first one
watch(
  () => props.groups,
  () => {
    if (activeTabIndex.value >= props.groups.length) {
      activeTabIndex.value = 0;
    }
  }
);

// Active group items (only the items from the selected tab)
const activeGroupItems = computed(() => {
  if (!hasGroups.value) return props.items;
  return props.groups[activeTabIndex.value]?.items || [];
});

// Flatten groups to get all items for keyboard navigation
const allItems = computed(() => {
  if (hasGroups.value) {
    return activeGroupItems.value;
  }
  return props.items;
});

const adjustScroll = () => {
  nextTick(() => {
    const container = mentionsListContainerRef.value;
    if (!container) return;
    const selectedElement = container.querySelector(
      `#mention-item-${selectedIndex.value}`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  });
};

const onSelect = () => {
  emit('mentionSelect', allItems.value[selectedIndex.value]);
};

useKeyboardNavigableList({
  items: computed(() => allItems.value),
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(
  () => allItems.value,
  newItems => {
    if (newItems.length < selectedIndex.value + 1) {
      selectedIndex.value = 0;
    }
  }
);

watch(selectedIndex, adjustScroll);

// Reset selected index when switching tabs
watch(activeTabIndex, () => {
  selectedIndex.value = 0;
});

const onHover = index => {
  selectedIndex.value = index;
};

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

const variableKey = (item = {}) => {
  return props.type === 'variable' ? `{{${item.label}}}` : `/${item.label}`;
};
</script>

<template>
  <div ref="mentionsListContainerRef"
    class="bg-n-solid-1 p-1 rounded-xl overflow-auto absolute w-full z-20 shadow-md left-0 bottom-full max-h-[22rem] border border-solid border-n-strong mention--box">
    <div v-if="hasGroups && groups.length > 1" class="flex gap-0.5 px-1 pt-1 pb-1.5 border-b border-n-weak mb-1 overflow-x-auto">
      <button
        v-for="(group, idx) in groups"
        :key="group.label"
        class="px-2.5 py-1 text-xs font-medium rounded-lg whitespace-nowrap transition-colors cursor-pointer"
        :class="{
          'bg-n-brand text-white': idx === activeTabIndex,
          'text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12': idx !== activeTabIndex,
        }"
        @click.stop="activeTabIndex = idx"
      >
        {{ group.label }}
        <span
          class="ml-1 text-[10px] opacity-70"
        >{{ group.items.length }}</span>
      </button>
    </div>

    <div v-if="hasGroups && groups.length === 1" class="px-3 py-1.5 text-xs font-semibold text-n-slate-10 uppercase tracking-wider select-none">
      {{ groups[0].label }}
    </div>

    <ul v-if="hasGroups" class="mb-0 vertical dropdown menu">
      <woot-dropdown-item v-for="(item, iIdx) in activeGroupItems" :id="`mention-item-${iIdx}`"
        :key="item.key" class="!mb-1" @mouseover="onHover(iIdx)">
        <button
          class="flex rounded-lg group flex-col gap-0.5 overflow-hidden cursor-pointer items-start px-3 py-2 justify-center w-full h-full text-left hover:bg-n-alpha-black2"
          :class="{
            'bg-n-alpha-black2': iIdx === selectedIndex,
          }" @click="onListItemSelection(iIdx)">
          <p class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
            :class="{
              'text-n-slate-12': iIdx === selectedIndex,
            }">
            {{ getPlainText(item.description) }}
          </p>
          <p class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
            :class="{
              'text-n-slate-12': iIdx === selectedIndex,
            }">
            {{ variableKey(item) }}
          </p>
        </button>
      </woot-dropdown-item>
    </ul>

    <template v-else>
      <ul class="mb-0 vertical dropdown menu">
        <woot-dropdown-item v-for="(item, index) in items" :id="`mention-item-${index}`" :key="item.key" class="!mb-1"
          @mouseover="onHover(index)">
          <button
            class="flex rounded-lg group flex-col gap-0.5 overflow-hidden cursor-pointer items-start px-3 py-2 justify-center w-full h-full text-left hover:bg-n-alpha-black2"
            :class="{
              'bg-n-alpha-black2': index === selectedIndex,
            }" @click="onListItemSelection(index)">
            <slot :item="item" :index="index" :selected="index === selectedIndex">
              <p class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }">
                {{ getPlainText(item.description) }}
              </p>
              <p class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }">
                {{ variableKey(item) }}
              </p>
            </slot>
          </button>
        </woot-dropdown-item>
      </ul>
    </template>
  </div>
</template>

<style scoped lang="scss">
.mention--box {
  .dropdown-menu__item:last-child>button {
    @apply border-0;
  }
}

.canned-item__button::v-deep .button__content {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}
</style>
